import { useState, useEffect } from "react";
import { Row, Col, Card, message, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
    get_subscription_plansApi,
    create_subscription_planApi,
    update_subscription_planApi,
    deactivate_subscription_planApi,
    get_subscription_plan_metadataApi,
} from "../api/admin.billing.apis";
import {
    SubscriptionPlansTable,
    CreateSubscriptionPlanModal,
    EditSubscriptionPlanModal,
} from "../components/subscription-plans-management";

function SubscriptionPlansManagement() {
    const [plans, setPlans] = useState([]);
    const [metadata, setMetadata] = useState({ features: [], providers: [] });
    const [loading, setLoading] = useState(true);

    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    useEffect(() => {
        fetchPlans();
        fetchMetadata();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await get_subscription_plansApi();
            if (response.data.status === "success") {
                const list = response.data.data?.plans || [];
                setPlans(list.map((p) => ({ ...p, key: p._id })));
            } else {
                message.error(response.data.message || "Failed to load subscription plans");
            }
        } catch (error) {
            console.error(error);
            message.error("Error loading subscription plans");
        } finally {
            setLoading(false);
        }
    };

    const fetchMetadata = async () => {
        try {
            const response = await get_subscription_plan_metadataApi();
            if (response.data.status === "success") {
                setMetadata(response.data.data || { features: [], providers: [] });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleCreate = async (values) => {
        try {
            setCreateLoading(true);
            const response = await create_subscription_planApi(values);
            if (response.data.status === "success") {
                message.success("Subscription plan created");
                setCreateModalVisible(false);
                fetchPlans();
            } else {
                message.error(response.data.message || "Failed to create plan");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to create subscription plan");
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEdit = async (values) => {
        try {
            setEditLoading(true);
            const response = await update_subscription_planApi(selectedPlan._id, values);
            if (response.data.status === "success") {
                message.success("Subscription plan updated");
                setEditModalVisible(false);
                setSelectedPlan(null);
                fetchPlans();
            } else {
                message.error(response.data.message || "Failed to update plan");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to update subscription plan");
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeactivate = async (plan) => {
        try {
            const response = await deactivate_subscription_planApi(plan._id);
            if (response.data.status === "success") {
                message.success("Plan deactivated");
                fetchPlans();
            } else {
                message.error(response.data.message || "Failed to deactivate plan");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to deactivate plan");
        }
    };

    return (
        <>
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Card
                        title="Subscription Plans"
                        extra={
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateModalVisible(true)}
                            >
                                Create Plan
                            </Button>
                        }
                    >
                        <SubscriptionPlansTable
                            plans={plans}
                            loading={loading}
                            onEdit={(plan) => {
                                setSelectedPlan(plan);
                                setEditModalVisible(true);
                            }}
                            onDeactivate={handleDeactivate}
                        />
                    </Card>
                </Col>
            </Row>

            <CreateSubscriptionPlanModal
                visible={createModalVisible}
                loading={createLoading}
                metadata={metadata}
                onSubmit={handleCreate}
                onClose={() => setCreateModalVisible(false)}
            />

            <EditSubscriptionPlanModal
                visible={editModalVisible}
                loading={editLoading}
                plan={selectedPlan}
                metadata={metadata}
                onSubmit={handleEdit}
                onClose={() => {
                    setEditModalVisible(false);
                    setSelectedPlan(null);
                }}
            />
        </>
    );
}

export default SubscriptionPlansManagement;
